import * as XLSX from 'xlsx'
import type { ExportColumn, ExportMetadataEntry } from './csv'

/**
 * Render rows to a single-sheet XLSX workbook (as an array-of-arrays,
 * so cell types survive — numbers stay numbers, unlike the CSV writer
 * which stringifies everything). Same `columnKeys` subset/order and
 * `metadata` leading-lines contract as `rowsToCsv` in `csv.ts`, so the
 * two formats are otherwise interchangeable from a caller's point of
 * view — pick one, get the same rows/columns/metadata either way.
 */
export function rowsToWorkbook<TRow>(
  rows: readonly TRow[],
  columns: readonly ExportColumn<TRow>[],
  columnKeys?: readonly string[],
  metadata?: readonly ExportMetadataEntry[],
): XLSX.WorkBook {
  const cols =
    columnKeys && columnKeys.length > 0
      ? columnKeys
          .map((k) => columns.find((c) => c.key === k))
          .filter((c): c is ExportColumn<TRow> => Boolean(c))
      : columns

  const aoa: unknown[][] = []
  if (metadata?.length) {
    for (const m of metadata) aoa.push([`${m.label}:`, m.value])
    aoa.push([])
  }
  aoa.push(cols.map((c) => c.header))
  for (const r of rows) aoa.push(cols.map((c) => c.value(r) ?? null))

  const sheet = XLSX.utils.aoa_to_sheet(aoa)
  const book = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(book, sheet, 'Export')
  return book
}

export interface DownloadXlsxOptions {
  filenameBase: string
  columnKeys?: readonly string[]
  metadata?: readonly ExportMetadataEntry[]
}

export function downloadXlsx<TRow>(
  rows: readonly TRow[],
  columns: readonly ExportColumn<TRow>[],
  opts: DownloadXlsxOptions,
): void {
  const book = rowsToWorkbook(rows, columns, opts.columnKeys, opts.metadata)
  const today = new Date().toISOString().slice(0, 10)
  const name = `${opts.filenameBase}-${today}.xlsx`
  XLSX.writeFile(book, name)
}
